/**
 * Modelo do objeto `playback` devolvido por `POST /video/{video_id}/playback-session`.
 * Contrato: docs/frontend-hls-contract.md (este repo) e, do lado servidor,
 * Api FTCloud/docs/backend-hls-contract.md, seção "Contrato HTTP".
 *
 * Campos em snake_case — é o shape exato da API (não traduzir para camelCase). Ver
 * task-front-brief, ruling #4: um nome de campo errado aqui vira `undefined` silencioso
 * em runtime, não erro de compilação — por isso o spec do service prova o mapeamento com
 * um JSON literal copiado do contrato em vez de confiar só no tipo.
 */
export type PlaybackStatus = 'PENDING' | 'RUNNING' | 'READY' | 'FAILED';

export type PlaybackResolution = '1080p' | '720p' | '480p' | '360p' | '240p' | '144p';

export interface PlaybackError {
  code: string;
  retryable: boolean;
  unsupported_resolutions?: string[];
}

export interface VideoPlayback {
  /**
   * `number`, não o literal `1`: o contrato manda tratar uma versão de schema
   * desconhecida com telemetria + fallback visual, não com um tipo que só aceita a
   * versão atual — ver docs/frontend-hls-contract.md, "Modelo recebido".
   */
  schema_version: number;
  status: PlaybackStatus;
  media_type: string;
  hls_url: string | null;
  poster_url: string | null;
  resolutions: PlaybackResolution[];
  started_at: string | null;
  updated_at: string | null;
  completed_at: string | null;
  execution_duration_seconds: number | null;
  retry_after_seconds: number | null;
  authorization_expires_at: string | null;
  error: PlaybackError | null;
}

/** Envelope de `POST /video/{video_id}/playback-session`. */
export interface VideoPlaybackSessionResponse {
  video_id: string;
  playback: VideoPlayback;
}

/** Única versão de schema que este cliente sabe interpretar hoje. */
export const SUPPORTED_PLAYBACK_SCHEMA_VERSION = 1;
